import { FC } from 'react';
import { Alert, Call } from '@ixigo/icons';
import ActionItem from './ActionItem';
import BottomsheetHeader from '../../BottomsheetHeader';
import { TestId } from '../../../enums/test';
import { createEscalationTicket, EscalationTicketData } from '../../../apis/aiSmartSupport';
import useUserInfo from '../../../hooks/useUserInfo';
import { BookingDetail, TimelineData, FdTicket } from '../timelineTypes';
import logger from '../../../utils/logger';

interface ContactSupportSheetProps {
    onClose: () => void;
    onShowToast: (message: string, icon: string) => void;
    bookingDetail?: BookingDetail;
    timelineData?: TimelineData;
    botSessions?: any[];
    callHistory?: any[];
    callTitle?: string;
    callSubTitle?: string;
    getValidPhoneNumber: () => string;
    fdTicket?: FdTicket;
}

const ContactSupportSheet: FC<ContactSupportSheetProps> = ({
    onClose,
    onShowToast,
    bookingDetail,
    timelineData,
    botSessions,
    callHistory,
    callTitle,
    callSubTitle,
    getValidPhoneNumber,
    fdTicket,
}) => {
    const { userInfo } = useUserInfo();

    const getValidProduct = (product?: string): string => {
        if (!product) return 'Flights';

        const productLower = product.toLowerCase();

        if (productLower.includes('train')) return 'Trains';
        if (productLower.includes('flight')) return 'Flights';
        if (productLower.includes('bus')) return 'Buses';
        if (productLower.includes('hotel')) return 'Hotels';

        // Default to Flights if no match found
        return 'Flights';
    };

    const generateEscalationSummary = (): string => {
        let summary = '';

        // Add timeline summary
        if (timelineData?.summary) {
            summary += `Timeline Summary: ${timelineData.summary}\n\n`;
        }

        // Add issues summary
        if (timelineData?.issues && timelineData.issues.length > 0) {
            summary += 'Issues:\n';
            timelineData.issues.forEach((issue, index) => {
                summary += `${index + 1}. ${issue.issue} - ${issue.description}\n`;
            });
            summary += '\n';
        }

        // Add bot sessions details
        if (botSessions && botSessions.length > 0) {
            summary += `Bot Sessions (${botSessions.length} session(s)):\n`;
            botSessions.forEach((session, index) => {
                summary += `${index + 1}. Session ID: ${session._id}\n`;
                summary += `   Flow: ${session.dialogueFlowName}\n`;
                summary += `   Description: ${session.description || 'N/A'}\n`;
            });
        }

        // Add call history details
        if (callHistory && callHistory.length > 0) {
            summary += `Call History (${callHistory.length} call(s)):\n`;
            callHistory.forEach((call, index) => {
                summary += `${index + 1}. Heading: ${call.heading}\n`;
                summary += `   Summary: ${call.summary}\n\n`;
            });
        }

        return summary || 'No additional summary available';
    };

    const handleEscalate = async () => {
        try {
            // Use API data if available, otherwise fallback to hardcoded values
            const escalationData: EscalationTicketData = {
                email: userInfo.email || '',
                phone: userInfo.mobile || '+12345678',
                name: userInfo.name || 'User',
                description: `${
                    fdTicket?.description?.replace('BOOKINGID', bookingDetail?.bookingId || '') ||
                    `Booking id : ${bookingDetail?.bookingId || ''}\nCall and chat summary :`
                } ${generateEscalationSummary()}`,
                priority: fdTicket?.priority || 1,
                source: fdTicket?.source || 101,
                status: fdTicket?.status || 2,
                subject:
                    fdTicket?.subject?.replace('BOOKINGID', bookingDetail?.bookingId || '') ||
                    `smart support escalation - ${bookingDetail?.bookingId || ''}`,
                type: fdTicket?.type || 'Booking Related',
                product: getValidProduct(bookingDetail?.product),
                issueType: fdTicket?.issueType || 'Booking Related',
                tags: fdTicket?.tags || ['escalation', 'smart-support'],
            };

            // Call the escalation API
            await createEscalationTicket(escalationData);

            onClose();
            onShowToast('We understand your concern and it has been escalated. Thank you for your patience.', 'alert');
        } catch (error) {
            logger({ id: 'ESCALATION_FAILED', error: error instanceof Error ? error.message : 'Unknown error' });
            onShowToast('Failed to escalate the issue. Please try again.', 'error');
        }
    };

    const handleCallbackRequest = () => {
        onClose();

        const phoneNumber = getValidPhoneNumber();

        // Only open phone dialer if we have a valid phone number
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;
        }
    };

    const hasValidPhoneNumbers = !!getValidPhoneNumber();

    return (
        <div
            className='flex flex-col bg-white rounded-t-[10px] overflow-hidden w-full'
            data-testid={TestId.CONTACT_SUPPORT_SHEET}
        >
            <BottomsheetHeader title='Need Help?' onClose={onClose} />
            <div className='flex flex-col w-full'>
                <ActionItem
                    icon={<Alert className='text-[#5E616E]' width={24} height={24} />}
                    title='Escalate the issue'
                    description='Your issue will be prioritized'
                    onClick={handleEscalate}
                    showBorder
                    iconContainerClassName='bg-error-100'
                />
                {hasValidPhoneNumbers && (
                    <ActionItem
                        icon={<Call className='text-zinc-500' width={24} height={24} />}
                        title={callTitle || 'Call support'}
                        description={callSubTitle || 'Speak directly with our customer care team'}
                        onClick={handleCallbackRequest}
                    />
                )}
            </div>
        </div>
    );
};

export default ContactSupportSheet;
